/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    Typography,
    createTheme,
    ThemeProvider
} from '@mui/material';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import {
    TOAST_MESSAGE_SEVERITY_ERROR,
    TOAST_MESSAGE_SEVERITY_SUCCESS
} from 'modules/layout/types';
import { setPageTitle, showToast } from 'modules/layout/layout.actions';

import {
    FETCH_CLIENT_USERS_MIGRATION_FAILURE,
    POST_CLIENT_USERS_MIGRATION_FAILURE
} from './store/clientUsersMigrationTypes';

import {
    getClientUsersMigration,
    postClientUserMigration,
    getClientContactMigrationHistory
} from './store/clientUsersMigration.actions';

import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import { withStyles } from '@material-ui/core';


const PAGE_SIZE = 30;

const styles = theme => ({
    header: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2)
    },
    title: {
        fontWeight: 500
    },
    emailField: {
        minWidth: 400
    }
});

const getContactKey = contact =>
    contact.legacyClientContactKey ||
    `${contact.clientRecordId}-${contact.officeEmail || contact.firstName}-${contact.lastName}`;

const getContactName = contact =>
    [contact.firstName, contact.lastName].filter(Boolean).join(' ') || '-';

const getMissingUserName = user =>
    [user.firstName, user.lastName].filter(Boolean).join(' ') || '-';

const normalizePlanResponse = response => {
    const plan = response?.response || response || {};

    return {
        contactsToMigrate: plan.contactsToMigrate || [],
        apiUsersMissingFromAuth0: plan.apiUsersMissingFromAuth0 || []
    };
};

const normalizeHistoryResponse = response => {
    const history = response?.response || response || [];

    return Array.isArray(history) ? history : [];
};

const normalizeSaveResponse = response =>
    response?.response || response || {};

const formatImportedOn = importedOn => {
    if (!importedOn) {
        return '-';
    }

    return new Date(importedOn).toLocaleString();
};

const getDivisionCount = contact => {
    if (Array.isArray(contact.divisions)) {
        return contact.divisions.length;
    }

    if (Array.isArray(contact.clientDivisionIds)) {
        return contact.clientDivisionIds.length;
    }

    return 0;
};

const getDivisionNames = contact => {
    if (Array.isArray(contact.divisions) && contact.divisions.length > 0) {
        return contact.divisions
            .map(division => division.divisionName || division.clientDivisionId)
            .filter(Boolean)
            .join(', ');
    }

    if (
        Array.isArray(contact.divisionNames) &&
        contact.divisionNames.length > 0
    ) {
        return contact.divisionNames.join(', ');
    }

    if (
        Array.isArray(contact.clientDivisionIds) &&
        contact.clientDivisionIds.length > 0
    ) {
        return contact.clientDivisionIds.join(', ');
    }

    return 'No divisions';
};

const sortContacts = contacts =>
    [...contacts].sort((first, second) => {
        const firstClientName =
            first.clientName || `Client ${first.clientRecordId || ''}`;

        const secondClientName =
            second.clientName || `Client ${second.clientRecordId || ''}`;

        const clientCompare = firstClientName.localeCompare(secondClientName);

        if (clientCompare !== 0) {
            return clientCompare;
        }

        return getContactName(first).localeCompare(getContactName(second));
    });

const groupContactsByClient = contacts =>
    contacts.reduce((groups, contact) => {
        const key = contact.clientRecordId || 'unknown';

        if (!groups[key]) {
            groups[key] = {
                clientRecordId: contact.clientRecordId,
                clientName:
                    contact.clientName ||
                    `Client ${contact.clientRecordId || 'Unknown'}`,
                contacts: []
            };
        }

        groups[key].contacts.push(contact);

        return groups;
    }, {});

const MissingAuth0Table = ({ users }) => {
    if (!users || users.length === 0) {
        return null;
    }

    return (
        <Box sx={{ mb: 4 }}>
            <Box sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                    API Users Missing from Auth0
                </Typography>

                <Typography variant="body2" color="text.secondary">
                    These contacts are marked as API users in MoveTrack, but no matching
                    Auth0 account was found by email.
                </Typography>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>MoveTrack Contact ID</TableCell>
                            <TableCell>Client ID</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {users.map(user => (
                            <TableRow
                                key={
                                    user.legacyClientContactKey ||
                                    `${user.clientId}-${user.email}`
                                }
                            >
                                <TableCell>
                                    {user.legacyClientContactKey || '-'}
                                </TableCell>

                                <TableCell>{user.clientId || '-'}</TableCell>

                                <TableCell>{getMissingUserName(user)}</TableCell>

                                <TableCell>{user.email || '-'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const SaveReport = ({ report }) => {
    if (!report) {
        return null;
    }

    const batchNumber =
        report.batchNumber || report.clientContactMigrationBatchId;

    const requestedCount = report.requestedCount;

    const migratedCount =
        report.migratedCount ?? report.successCount;

    const failedCount = report.failedCount;

    const hasFailures =
        typeof failedCount === 'number' && failedCount > 0;

    return (
        <Alert severity={hasFailures ? 'warning' : 'success'} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700}>
                Migration save completed
            </Typography>

            <Typography variant="body2">
                {batchNumber ? `Batch ${batchNumber}. ` : ''}
                {typeof requestedCount === 'number'
                    ? `${requestedCount} requested. `
                    : ''}
                {typeof migratedCount === 'number'
                    ? `${migratedCount} migrated. `
                    : ''}
                {typeof failedCount === 'number'
                    ? `${failedCount} failed.`
                    : ''}
            </Typography>
        </Alert>
    );
};

const ContactRows = ({
    contacts,
    selectedContacts,
    onSelectContact,
    clientName,
    clientRecordId,
}) => (
    <>
        {contacts.map(contact => {
            const key = getContactKey(contact);
            const selected = Boolean(selectedContacts[key]);
            const divisionCount = getDivisionCount(contact);
            const divisionNames = getDivisionNames(contact);

            return (
                <TableRow key={key} hover selected={selected}>
                    <TableCell padding="checkbox">
                        <Checkbox
                            checked={selected}
                            onChange={event =>
                                onSelectContact(contact, event.target.checked)
                            }
                        />
                    </TableCell>
                    <TableCell>
                        {clientName || '-'}
                    </TableCell>
                    <TableCell>
                        {clientRecordId || '-'}
                    </TableCell>

                    <TableCell>
                        {getContactName(contact)}
                    </TableCell>

                    <TableCell>{contact.jobTitle || '-'}</TableCell>
                    <TableCell>{contact.officeEmail || '-'}</TableCell>
                    <TableCell>{contact.officePhone || '-'}</TableCell>
                    <TableCell>
                        <Tooltip title={divisionNames}>
                            <Chip
                                label={`${divisionCount} division${divisionCount === 1 ? '' : 's'
                                    }`}
                                size="small"
                                color={divisionCount > 0 ? 'info' : 'default'}
                                variant="outlined"
                            />
                        </Tooltip>
                    </TableCell>
                    <TableCell>
                        {contact.auth0UserId ? (
                            <Tooltip title={contact.auth0UserId}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        maxWidth: 220,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {contact.auth0UserId}
                                </Typography>
                            </Tooltip>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Not an API user
                            </Typography>
                        )}
                    </TableCell>
                </TableRow>
            );
        })}
    </>
);

const ContactMigrationTable = ({
    clientGroups,
    visibleContacts,
    selectedContacts,
    onSelectContact,
    onSelectAllVisible
}) => {
    const allVisibleSelected =
        visibleContacts.length > 0 &&
        visibleContacts.every(contact => selectedContacts[getContactKey(contact)]);

    const someVisibleSelected =
        visibleContacts.some(contact => selectedContacts[getContactKey(contact)]) &&
        !allVisibleSelected;

    return (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell padding="checkbox">
                            <Checkbox
                                checked={allVisibleSelected}
                                indeterminate={someVisibleSelected}
                                onChange={event =>
                                    onSelectAllVisible(event.target.checked)
                                }
                                disabled={visibleContacts.length === 0}
                            />
                        </TableCell>

                        <TableCell>Client Name</TableCell>
                        <TableCell>Client Record ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Job Title</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Divisions</TableCell>
                        <TableCell>Auth0 ID</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {visibleContacts.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={8} align="center">
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ py: 4 }}
                                >
                                    No client contacts are ready for migration.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        clientGroups.map(group => (
                            <React.Fragment
                                key={group.clientRecordId || group.clientName}
                            >
                                {/* <TableRow>
                                    <TableCell colSpan={8} sx={{ bgcolor: '#f5f5f5' }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                                flexWrap: 'wrap'
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={700}>
                                                    {group.clientName}
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Client Record ID: {group.clientRecordId || '-'}
                                                </Typography>
                                            </Box>

                                            <Chip
                                                label={`${group.contacts.length} contact${group.contacts.length === 1 ? '' : 's'
                                                    }`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow> */}

                                <ContactRows
                                    contacts={group.contacts}
                                    selectedContacts={selectedContacts}
                                    onSelectContact={onSelectContact}
                                    clientName={group.clientName}
                                    clientRecordId={group.clientRecordId}

                                />
                            </React.Fragment>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const MigrationHistoryTable = ({ records }) => {
    if (!records || records.length === 0) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    No migrated client contacts were found.
                </Typography>
            </Paper>
        );
    }

    return (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Batch</TableCell>
                        <TableCell>Imported</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Job Title</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Divisions</TableCell>
                        <TableCell>Auth0 ID</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {records.map(record => {
                        const divisionNames = getDivisionNames(record);
                        const divisionCount =
                            Array.isArray(record.divisionNames)
                                ? record.divisionNames.length
                                : Array.isArray(record.divisions)
                                    ? record.divisions.length
                                    : 0;

                        return (
                            <TableRow
                                key={`${record.clientContactId}-${record.batchNumber}`}
                                hover
                            >
                                <TableCell>
                                    <Chip
                                        label={record.batchNumber || '-'}
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>

                                <TableCell>
                                    {formatImportedOn(record.importedOn)}
                                </TableCell>

                                <TableCell>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>
                                            {record.clientName ||
                                                `Client ${record.clientRecordId || '-'}`}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Record ID: {record.clientRecordId || '-'}
                                        </Typography>
                                    </Box>
                                </TableCell>

                                <TableCell>
                                    <Box>
                                        <Typography >
                                            {[record.firstName, record.lastName]
                                                .filter(Boolean)
                                                .join(' ') || '-'}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Record ID: {record.clientContactId || '-'}
                                        </Typography>
                                    </Box>

                                </TableCell>

                                <TableCell>{record.jobTitle || '-'}</TableCell>

                                <TableCell>{record.officeEmail || '-'}</TableCell>

                                <TableCell>{record.officePhone || '-'}</TableCell>

                                <TableCell>
                                    <Tooltip title={divisionNames}>
                                        <Chip
                                            label={`${divisionCount} division${divisionCount === 1 ? '' : 's'
                                                }`}
                                            size="small"
                                            color={divisionCount > 0 ? 'info' : 'default'}
                                            variant="outlined"
                                        />
                                    </Tooltip>
                                </TableCell>

                                <TableCell>
                                    {record.auth0UserId ? (
                                        <Tooltip title={record.auth0UserId}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    maxWidth: 220,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {record.auth0UserId}
                                            </Typography>
                                        </Tooltip>
                                    ) : (
                                        '-'
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const ClientContactsMigrationView = props => {
    const {
        setPageTitle,
        showToast,
        getClientUsersMigration,
        postClientUserMigration,
        getClientContactMigrationHistory
    } = props;

    const theme = createTheme({
        palette: {
            primary: COLOR_PRIMARY,
            secondary: COLOR_SECONDARY,
            warning: COLOR_WARNING
        }
    });

    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const [contacts, setContacts] = useState([]);
    const [missingAuth0Users, setMissingAuth0Users] = useState([]);
    const [selectedContacts, setSelectedContacts] = useState({});
    const [saveReport, setSaveReport] = useState(null);

    const [historyRecords, setHistoryRecords] = useState([]);
    const [hasLoadedPlan, setHasLoadedPlan] = useState(false);
    const [hasLoadedHistory, setHasLoadedHistory] = useState(false);

    const [page, setPage] = useState(0);

    useEffect(() => {
        setPageTitle('Client Contact Migration');
    }, []);

    const sortedContacts = useMemo(
        () => sortContacts(contacts),
        [contacts]
    );

    const pageCount = Math.max(
        1,
        Math.ceil(sortedContacts.length / PAGE_SIZE)
    );

    const visibleContacts = useMemo(() => {
        const startIndex = page * PAGE_SIZE;

        return sortedContacts.slice(
            startIndex,
            startIndex + PAGE_SIZE
        );
    }, [sortedContacts, page]);

    const visibleClientGroups = useMemo(() => {
        const grouped = groupContactsByClient(visibleContacts);

        return Object.values(grouped).sort((first, second) =>
            first.clientName.localeCompare(second.clientName)
        );
    }, [visibleContacts]);

    const selectedContactList = useMemo(
        () =>
            sortedContacts.filter(
                contact => selectedContacts[getContactKey(contact)]
            ),
        [sortedContacts, selectedContacts]
    );

    const loadContacts = async () => {
        setIsLoading(true);
        setSaveReport(null);

        try {
            const response = await getClientUsersMigration();

            if (
                response?.type === FETCH_CLIENT_USERS_MIGRATION_FAILURE
            ) {
                showToast('Error loading client contacts for migration', {
                    severity: TOAST_MESSAGE_SEVERITY_ERROR
                });

                return;
            }

            const plan = normalizePlanResponse(response);

            setContacts(plan.contactsToMigrate);
            setMissingAuth0Users(plan.apiUsersMissingFromAuth0);
            setSelectedContacts({});
            setPage(0);
            setHasLoadedPlan(true);
        } catch {
            showToast('Error loading client contacts for migration', {
                severity: TOAST_MESSAGE_SEVERITY_ERROR
            });
        } finally {
            setIsLoading(false);
        }
    };

    const loadHistory = async () => {
        setIsLoading(true);

        try {
            const response = await getClientContactMigrationHistory();

            if (
                response?.type === FETCH_CLIENT_USERS_MIGRATION_FAILURE
            ) {
                showToast('Error loading client contact migration history', {
                    severity: TOAST_MESSAGE_SEVERITY_ERROR
                });

                return;
            }

            const history = normalizeHistoryResponse(response);

            setHistoryRecords(history);
            setHasLoadedHistory(true);
        } catch {
            showToast('Error loading client contact migration history', {
                severity: TOAST_MESSAGE_SEVERITY_ERROR
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleTabChange = async (_, newValue) => {
        setActiveTab(newValue);

        if (newValue === 1 && !hasLoadedHistory) {
            await loadHistory();
        }
    };

    const handleSelectContact = (contact, checked) => {
        const key = getContactKey(contact);

        setSelectedContacts(previous => ({
            ...previous,
            [key]: checked
        }));
    };

    const handleSelectAllVisible = checked => {
        setSelectedContacts(previous => {
            const next = { ...previous };

            visibleContacts.forEach(contact => {
                next[getContactKey(contact)] = checked;
            });

            return next;
        });
    };

    const handleSave = async () => {
        if (selectedContactList.length === 0) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await postClientUserMigration({
                contacts: selectedContactList
            });

            if (
                response?.type === POST_CLIENT_USERS_MIGRATION_FAILURE
            ) {
                showToast('Error migrating selected client contacts', {
                    severity: TOAST_MESSAGE_SEVERITY_ERROR
                });

                return;
            }

            const report = normalizeSaveResponse(response);

            setSaveReport(report);

            showToast('Selected client contacts were migrated successfully', {
                severity: TOAST_MESSAGE_SEVERITY_SUCCESS
            });

            const successfulResults =
                report.results?.filter(result => result.success) || [];

            if (successfulResults.length > 0) {
                const migratedKeys = new Set(
                    successfulResults.map(result => {
                        if (result.legacyClientContactKey) {
                            return result.legacyClientContactKey;
                        }

                        return `${result.clientRecordId}-${result.officeEmail}`;
                    })
                );

                setContacts(previous =>
                    previous.filter(contact => {
                        const contactKey = getContactKey(contact);
                        const fallbackKey = `${contact.clientRecordId}-${contact.officeEmail}`;

                        return (
                            !migratedKeys.has(contactKey) &&
                            !migratedKeys.has(fallbackKey)
                        );
                    })
                );
            } else {
                const selectedKeys = new Set(
                    selectedContactList.map(contact => getContactKey(contact))
                );

                setContacts(previous =>
                    previous.filter(
                        contact => !selectedKeys.has(getContactKey(contact))
                    )
                );
            }

            setSelectedContacts({});
            setPage(0);

            // Refresh the history data after a successful save.
            const historyResponse =
                await getClientContactMigrationHistory();

            if (
                historyResponse?.type !== FETCH_CLIENT_USERS_MIGRATION_FAILURE
            ) {
                setHistoryRecords(
                    normalizeHistoryResponse(historyResponse)
                );
                setHasLoadedHistory(true);
            }
        } catch {
            showToast('Error migrating selected client contacts', {
                severity: TOAST_MESSAGE_SEVERITY_ERROR
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ThemeProvider theme={theme}>

            <Box sx={{ p: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        mb: 2,
                        flexWrap: 'wrap'
                    }}
                >
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Client Contact Migration
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            {contacts.length} contacts ready for migration
                            {missingAuth0Users.length > 0 &&
                                `, ${missingAuth0Users.length} API users missing from Auth0`}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            sx={{
                                maxHeight: '40px',
                                marginRight: '10px',
                                marginTop: '8px'
                            }}
                            onClick={loadContacts}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Loading...' : 'Load Client Contacts'}
                        </Button>

                        <Button
                            color="secondary"
                            variant="contained"
                            sx={{
                                maxHeight: '40px',
                                marginRight: '10px',
                                marginTop: '8px',
                                backgroundColor: '#5D9878',
                                '&:hover': { backgroundColor: '#528569' }
                            }}
                            onClick={handleSave}
                            disabled={
                                isLoading || selectedContactList.length === 0
                            }
                        >
                            Migrate Selected ({selectedContactList.length})
                        </Button>
                    </Box>
                </Box>

                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{ mb: 3 }}
                >
                    <Tab label="Migration Plan" />
                    <Tab label="Migration History" />
                </Tabs>

                <SaveReport report={saveReport} />

                {activeTab === 0 && (
                    <>
                        {hasLoadedPlan ? (
                            <>
                                <MissingAuth0Table users={missingAuth0Users} />

                                <Divider sx={{ mb: 3 }} />

                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 1,
                                        gap: 2,
                                        flexWrap: 'wrap'
                                    }}
                                >
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>
                                            Contacts Ready for Migration
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Grouped by client. Hover over the division count
                                            to see division names.
                                        </Typography>
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Showing{' '}
                                        {sortedContacts.length === 0
                                            ? 0
                                            : page * PAGE_SIZE + 1}
                                        -
                                        {Math.min(
                                            (page + 1) * PAGE_SIZE,
                                            sortedContacts.length
                                        )}{' '}
                                        of {sortedContacts.length}
                                    </Typography>
                                </Box>

                                <ContactMigrationTable
                                    clientGroups={visibleClientGroups}
                                    visibleContacts={visibleContacts}
                                    selectedContacts={selectedContacts}
                                    onSelectContact={handleSelectContact}
                                    onSelectAllVisible={handleSelectAllVisible}
                                />

                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: 2,
                                        mt: 2
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            setPage(previous => Math.max(0, previous - 1))
                                        }
                                        disabled={page === 0 || isLoading}
                                    >
                                        Previous
                                    </Button>

                                    <Typography variant="body2">
                                        Page {Math.min(page + 1, pageCount)} of {pageCount}
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            setPage(previous =>
                                                Math.min(pageCount - 1, previous + 1)
                                            )
                                        }
                                        disabled={
                                            page >= pageCount - 1 || isLoading
                                        }
                                    >
                                        Next
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Click “Load Client Contacts” to retrieve migration
                                    candidates.
                                </Typography>
                            </Paper>
                        )}
                    </>
                )}

                {activeTab === 1 && (
                    <Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2,
                                gap: 2,
                                flexWrap: 'wrap'
                            }}
                        >
                            <Box>
                                <Typography variant="h6" fontWeight={700}>
                                    Migration History
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Previously migrated client contacts, newest batches
                                    first.
                                </Typography>
                            </Box>

                            <Button
                                variant="outlined"
                                onClick={loadHistory}
                                disabled={isLoading}
                            >
                                Refresh History
                            </Button>
                        </Box>

                        {!hasLoadedHistory && isLoading ? (
                            <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Loading migration history...
                                </Typography>
                            </Paper>
                        ) : (
                            <MigrationHistoryTable records={historyRecords} />
                        )}
                    </Box>
                )}

                {isLoading && <FullscreenSpinner />}
            </Box>
        </ThemeProvider>
    );
};

export default compose(
    withStyles(styles),
    connect(null, {
        setPageTitle,
        showToast,
        getClientUsersMigration,
        postClientUserMigration,
        getClientContactMigrationHistory
    })
)(ClientContactsMigrationView);